import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import PageHeader from '../../../components/common/PageHeader';
import MetricsCard from '../../../components/common/MetricsCard';
import RevenueActivityChart from '../components/RevenueActivityChart';
import DashboardService from '../services/dashboardService';

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Τελευταίος μήνας' },
  { value: '6months', label: 'Τελευταίοι 6 μήνες' },
  { value: 'year', label: 'Τελευταίο έτος' },
];

const CLIENT_STATUS_OPTIONS = [
  { value: 'all', label: 'Όλοι (ενεργοί + ανενεργοί)' },
  { value: 'active', label: 'Ενεργοί' },
  { value: 'inactive', label: 'Ανενεργοί' },
];

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '0,00 €';
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const toYMD = (d) => (d ? format(d, 'yyyy-MM-dd') : null);
const isValidDate = (d) => d && d instanceof Date && !isNaN(d.getTime());
const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const computePresetRange = (preset) => {
  const dateTo = startOfToday();
  if (preset === 'month') {
    return { dateFrom: new Date(dateTo.getFullYear(), dateTo.getMonth(), 1), dateTo };
  }
  const monthsBack = preset === '6months' ? 5 : 11;
  return {
    dateFrom: new Date(dateTo.getFullYear(), dateTo.getMonth() - monthsBack, 1),
    dateTo,
  };
};

const isCommittedDate = (d) => (
  isValidDate(d) && d.getFullYear() >= 2010 && d.getFullYear() <= 2100
);

const parseYMD = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  return isCommittedDate(parsed) ? parsed : null;
};

const DASHBOARD_FILTERS_KEY = 'dashboard.revenueFilters';

const loadSavedDashboardFilters = () => {
  const fallback = { period: '6months', clientStatus: 'all', ...computePresetRange('6months') };
  try {
    const stored = localStorage.getItem(DASHBOARD_FILTERS_KEY);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    const clientStatus = CLIENT_STATUS_OPTIONS.some((opt) => opt.value === parsed.clientStatus)
      ? parsed.clientStatus
      : 'all';
    const savedPeriod = parsed.period;
    if (PERIOD_OPTIONS.some((opt) => opt.value === savedPeriod)) {
      return { period: savedPeriod, clientStatus, ...computePresetRange(savedPeriod) };
    }
    const dateFrom = parseYMD(parsed.dateFrom);
    const dateTo = parseYMD(parsed.dateTo);
    if (isCommittedDate(dateFrom) && isCommittedDate(dateTo)) {
      return { period: 'custom', clientStatus, dateFrom, dateTo };
    }
    return { ...fallback, clientStatus };
  } catch {
    return fallback;
  }
};

const DashboardPage = () => {
  const savedFilters = useMemo(() => loadSavedDashboardFilters(), []);
  const [period, setPeriod] = useState(savedFilters.period);
  const [clientStatus, setClientStatus] = useState(savedFilters.clientStatus);
  const [dateFrom, setDateFrom] = useState(savedFilters.dateFrom);
  const [dateTo, setDateTo] = useState(savedFilters.dateTo);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const requestIdRef = useRef(0);

  const useCustomRange = period === 'custom';
  const periodSelectValue = useCustomRange ? 'custom' : (period || '6months');

  const loadData = useCallback(async () => {
    const from = isCommittedDate(dateFrom) ? dateFrom : computePresetRange(period || '6months').dateFrom;
    const to = isCommittedDate(dateTo) ? dateTo : startOfToday();
    const params = {
      date_from: toYMD(from),
      date_to: toYMD(to),
      client_status: clientStatus || 'all',
    };

    const myId = ++requestIdRef.current;
    try {
      setLoading(true);
      const [revenueRes, subsRes] = await Promise.all([
        DashboardService.getRevenueActivity(params),
        DashboardService.getSubscriptionSummary({ client_status: clientStatus || 'all' }),
      ]);
      if (myId !== requestIdRef.current) return;
      setRevenueData(revenueRes);
      setSubscriptionSummary(subsRes);
    } catch (err) {
      if (myId !== requestIdRef.current) return;
      console.error('Error loading dashboard:', err);
      setRevenueData(null);
      setSubscriptionSummary(null);
    } finally {
      if (myId === requestIdRef.current) setLoading(false);
    }
  }, [period, dateFrom, dateTo, clientStatus]);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_FILTERS_KEY, JSON.stringify({
      period: useCustomRange ? 'custom' : (period || '6months'),
      clientStatus: clientStatus || 'all',
      dateFrom: isCommittedDate(dateFrom) ? toYMD(dateFrom) : null,
      dateTo: isCommittedDate(dateTo) ? toYMD(dateTo) : null,
    }));
  }, [period, dateFrom, dateTo, clientStatus, useCustomRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const rangeSubtitle = isCommittedDate(dateFrom) && isCommittedDate(dateTo)
    ? `${format(dateFrom, 'dd/MM/yyyy')} – ${format(dateTo, 'dd/MM/yyyy')}`
    : null;

  const handlePeriodChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') return;
    const range = computePresetRange(value);
    setPeriod(value);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
  };

  const handleDateFromChange = (d) => {
    if (d == null) {
      setDateFrom(null);
      setPeriod('custom');
      return;
    }
    if (!isCommittedDate(d)) return;
    setDateFrom(d);
    setPeriod('custom');
    if (!isCommittedDate(dateTo)) {
      setDateTo(startOfToday());
    }
  };

  const handleDateToChange = (d) => {
    if (d == null) {
      setDateTo(null);
      setPeriod('custom');
      return;
    }
    if (!isCommittedDate(d)) return;
    setDateTo(d);
    setPeriod('custom');
  };

  const dateFieldSx = {
    width: { xs: 148, sm: 164 },
    minWidth: { xs: 148, sm: 164 },
    flex: 'none',
  };

  return (
    <Box>
      <PageHeader
        title="Επισκόπηση Εσόδων"
        subtitle="Εισπράξεις (πληρωμές) vs Τιμολόγηση (αξία υπηρεσιών)"
        icon={<TrendingUpIcon />}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexWrap="wrap"
      >
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
          <InputLabel>Περίοδος</InputLabel>
          <Select
            value={periodSelectValue}
            label="Περίοδος"
            onChange={handlePeriodChange}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
            {useCustomRange && (
              <MenuItem value="custom">Προσαρμοσμένη</MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 230 }, flexShrink: 0 }}>
          <InputLabel>Πελάτες</InputLabel>
          <Select
            value={clientStatus}
            label="Πελάτες"
            onChange={(e) => setClientStatus(e.target.value)}
          >
            {CLIENT_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1} sx={{ width: 'auto', flexShrink: 0 }}>
          <DatePicker
            label="Από"
            value={dateFrom}
            onChange={handleDateFromChange}
            format="dd/MM/yyyy"
            referenceDate={startOfToday()}
            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
          />
          <DatePicker
            label="Έως"
            value={dateTo}
            onChange={handleDateToChange}
            format="dd/MM/yyyy"
            referenceDate={startOfToday()}
            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
          />
        </Stack>
      </Stack>

      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricsCard
            title="Εισπράξεις (περίοδος)"
            value={formatCurrency(revenueData?.total)}
            icon={<AccountBalanceIcon />}
            color="primary"
            subtitle={rangeSubtitle || 'Πληρωμές που εισπράχθηκαν'}
            details={[
              { label: 'Από συνδρομές', value: formatCurrency(revenueData?.subscription_total) },
              { label: 'Από εφάπαξ', value: formatCurrency(revenueData?.one_time_total) },
              ...(Number(revenueData?.unallocated_total) > 0
                ? [{ label: 'Χωρίς κατανομή', value: formatCurrency(revenueData.unallocated_total) }]
                : []),
            ]}
            loading={loading}
            formatNumber={false}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricsCard
            title="Τιμολόγηση (περίοδος)"
            value={formatCurrency(revenueData?.services_value_total)}
            icon={<ReceiptIcon />}
            color="warning"
            subtitle={
              rangeSubtitle
                ? `${rangeSubtitle} · από εκ των οποίων ΦΠΑ: ${formatCurrency(revenueData?.services_vat_total)}`
                : 'Αξία υπηρεσιών που δημιουργήθηκαν'
            }
            loading={loading}
            formatNumber={false}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricsCard
            title="Ετήσιο εισόδημα (σε ισχύ τώρα)"
            value={formatCurrency(subscriptionSummary?.annual_recurring_revenue)}
            icon={<TrendingUpIcon />}
            color="info"
            subtitle={`${subscriptionSummary?.active_subscriptions_count ?? 0} σε ισχύ τώρα`}
            loading={loading}
            formatNumber={false}
          />
        </Grid>
      </Grid>

      <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Εισπράξεις vs Τιμολόγηση ανά μήνα
            {rangeSubtitle && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 1 }, display: { xs: 'block', sm: 'inline' }, fontWeight: 400 }}>
                ({rangeSubtitle})
              </Typography>
            )}
          </Typography>
          <RevenueActivityChart data={revenueData} loading={loading} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;
